// src/components/Cart/CartDrawer.js
/**
 * CartDrawer — Desktop slide-in cart overlay (desktop only, hidden on mobile).
 *
 * Renders via React portal to document.body to avoid z-index stacking context
 * issues with parent components (RESEARCH.md Pattern 2).
 *
 * Animation: framer-motion AnimatePresence + DRAWER_VARIANTS for slide from right.
 * Close triggers: X button and Escape key.
 * Accessibility: non-modal dialog so the page behind remains interactive.
 */

import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaTimes } from "react-icons/fa";
import { useCart } from "../../context/CartContext";
import { useCartDrawer } from "../../context/CartDrawerContext";
import { useCartRecommendations } from "../../hooks/useCartRecommendations";
import { useIsDesktop } from "../../hooks/useMediaQuery";
import * as S from "./CartDrawer.styled";
import { formatLek } from "../../Utils/currency";
import { readAvailability } from "../../Utils/availability";

// ── Animation variants ───────────────────────────────────────────────────────
const DRAWER_VARIANTS = {
  hidden:  { x: "100%", opacity: 0 },
  visible: { x: "0%",   opacity: 1 },
  exit:    { x: "100%", opacity: 0 },
};

// Check once at module level to avoid per-render matchMedia calls.
const prefersReducedMotion =
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// ── Component ────────────────────────────────────────────────────────────────
const CartDrawer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isOpen, isPeeking, closeDrawer } = useCartDrawer();
  const {
    cartItems,
    addItemToCart,
    removeItemFromCart,
    updateItemQuantity,
  } = useCart();
  const { recs, loading: recsLoading } = useCartRecommendations();
  const isDesktop = useIsDesktop();

  // Desktop-only: on mobile the CartSheet renders instead. Gating in JS (not just
  // CSS display:none) prevents a duplicate cart dialog in the accessibility tree.
  const show = (isOpen || isPeeking) && isDesktop;

  // Escape key handler (D-03) — only active when the drawer is visible.
  useEffect(() => {
    if (!show) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") closeDrawer();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [show, closeDrawer]);

  // Body scroll lock (RESEARCH.md Pattern 4) — compensates for scrollbar width
  // to prevent layout shift when overflow is hidden.
  // Derived subtotal from cart items.
  const totalPrice = cartItems.reduce(
    (total, item) =>
      total + Number(item.price) * item.quantity,
    0
  );

  const handleContinueShopping = () => {
    closeDrawer();
    // Navigate after a tick so the drawer close doesn't swallow the route change.
    setTimeout(() => navigate("/baskets"), 0);
  };

  const handleCheckout = () => {
    closeDrawer();
    navigate("/checkout");
  };

  const handleAddRec = (rec) => {
    addItemToCart({
      id: rec.id,
      farmId: rec.farmId,
      name: rec.name,
      price: Number(rec.price),
      image: rec.image,
      availableQuantity: rec.availableQuantity ?? null,
      quantity: 1,
    });
  };

  const handleRecKeyDown = (event, rec) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleAddRec(rec);
    }
  };

  // Reduced motion: collapse animation duration to 0 for users who prefer it.
  const transitionDuration = prefersReducedMotion ? 0 : 0.25;

  return createPortal(
    <AnimatePresence>
      {show && (
        <S.Overlay>
          <motion.div
            role="dialog"
            aria-modal="false"
            aria-label={t("cart.drawer_label", "Shopping cart")}
            variants={DRAWER_VARIANTS}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{
              type: "tween",
              duration: transitionDuration,
              ease: [0.4, 0, 0.2, 1],
            }}
            style={{ width: "100%", height: "100%", pointerEvents: "none" }}
          >
            <S.DrawerPanel>
              {/* ── Header ── */}
              <S.DrawerHeader>
                <S.DrawerTitle>{t("cart.page_title", "Cart")}</S.DrawerTitle>
                <S.CloseButton
                  type="button"
                  onClick={closeDrawer}
                  aria-label={t("cart.close", "Close cart")}
                >
                  <FaTimes aria-hidden="true" />
                </S.CloseButton>
              </S.DrawerHeader>

              {/* ── Content: empty state or item list ── */}
              {cartItems.length === 0 ? (
                <S.EmptyWrap>
                  <S.EmptyHeading>
                    {t("cart.empty_heading", "Your cart is empty")}
                  </S.EmptyHeading>
                  <S.EmptyBody>
                    {t(
                      "cart.empty_body",
                      "Looks like you haven't added anything yet."
                    )}
                  </S.EmptyBody>
                  <S.CheckoutBtn
                    variant="primary"
                    size="md"
                    onClick={handleContinueShopping}
                  >
                    {t("cart.continue_shopping", "Continue Shopping")}
                  </S.CheckoutBtn>
                </S.EmptyWrap>
              ) : (
                <>
                  {/* ── Item list ── */}
                  <S.ItemList>
                    {cartItems.map((item) => {
                      const available = readAvailability(item);
                      const atCap =
                        available !== null && item.quantity >= available;
                      return (
                      <S.ItemRow key={item.id}>
                        <S.ItemThumb
                          src={item.image}
                          alt={item.name}
                          loading="lazy"
                        />
                        <S.ItemInfo>
                          <S.ItemName>{item.name}</S.ItemName>
                          <S.QtyControls>
                            <S.QtyBtn
                              type="button"
                              disabled={item.quantity <= 1}
                              onClick={() =>
                                updateItemQuantity(item.id, item.quantity - 1)
                              }
                              aria-label={t(
                                "cart.decrease_of",
                                "Decrease quantity of {{name}}",
                                { name: item.name }
                              )}
                            >
                              -
                            </S.QtyBtn>
                            <S.QtyValue>{item.quantity}</S.QtyValue>
                            <S.QtyBtn
                              type="button"
                              disabled={atCap}
                              onClick={() =>
                                updateItemQuantity(item.id, item.quantity + 1)
                              }
                              aria-label={t(
                                "cart.increase_of",
                                "Increase quantity of {{name}}",
                                { name: item.name }
                              )}
                            >
                              +
                            </S.QtyBtn>
                          </S.QtyControls>
                        </S.ItemInfo>
                        <S.ItemPrice>
                          {formatLek(Number(item.price) * item.quantity)}
                        </S.ItemPrice>
                        <S.RemoveBtn
                          type="button"
                          onClick={() => removeItemFromCart(item.id)}
                          aria-label={t(
                            "cart.remove_item",
                            "Remove {{name}} from cart",
                            { name: item.name }
                          )}
                        >
                          <FaTimes aria-hidden="true" />
                        </S.RemoveBtn>
                      </S.ItemRow>
                      );
                    })}
                  </S.ItemList>

                  {/* ── Recommendations placeholder (CART-05, Phase 20) ── */}
                  {(recsLoading || recs.length > 0) && (
                    <S.RecsSection>
                      <S.RecsHeading>
                        {t("cart.recs_heading", "You might also like")}
                      </S.RecsHeading>
                      <S.RecsGrid>
                        {recsLoading
                          ? [0, 1, 2].map((index) => (
                              <S.RecsSkeleton key={index} aria-busy="true" />
                            ))
                          : recs.map((rec) => (
                              <S.RecCard
                                key={rec.id}
                                role="button"
                                tabIndex={0}
                                aria-label={t(
                                  "cart.add_rec",
                                  "Add {{name}}",
                                  { name: rec.name }
                                )}
                                onClick={() => handleAddRec(rec)}
                                onKeyDown={(event) =>
                                  handleRecKeyDown(event, rec)
                                }
                              >
                                <S.RecThumb
                                  src={rec.image}
                                  alt={rec.name}
                                  loading="lazy"
                                />
                                <S.RecInfo>
                                  <S.RecName>{rec.name}</S.RecName>
                                  <S.RecPrice>
                                    {formatLek(rec.price)}
                                  </S.RecPrice>
                                </S.RecInfo>
                                <S.RecAddBtn
                                  type="button"
                                  aria-label={t(
                                    "cart.add_rec",
                                    "Add {{name}}",
                                    { name: rec.name }
                                  )}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleAddRec(rec);
                                  }}
                                >
                                  {t("cart.add", "Add")}
                                </S.RecAddBtn>
                              </S.RecCard>
                            ))}
                      </S.RecsGrid>
                    </S.RecsSection>
                  )}

                  {/* ── Footer ── */}
                  <S.DrawerFooter>
                    <S.SubtotalRow>
                      <S.SubtotalLabel>
                        {t("cart.subtotal", "Subtotal")}
                      </S.SubtotalLabel>
                      <S.SubtotalValue>{formatLek(totalPrice)}</S.SubtotalValue>
                    </S.SubtotalRow>
                    <S.SubtotalNote>
                      {t(
                        "cart.delivery_note",
                        "(Delivery calculated at checkout)"
                      )}
                    </S.SubtotalNote>
                    <S.CheckoutBtn
                      variant="primary"
                      size="md"
                      onClick={handleCheckout}
                    >
                      {t("cart.proceed_checkout", "Proceed to Checkout")}
                    </S.CheckoutBtn>
                  </S.DrawerFooter>
                </>
              )}
            </S.DrawerPanel>
          </motion.div>
        </S.Overlay>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default CartDrawer;
